/**
 * APAP (Acetaminophen) Scenario Definition
 *
 * PHARMACOKINETIC BASIS (see docs/pharmacokinetic-parameters.md):
 * - Pathway split at therapeutic: ~55% UGT / ~30% SULT / ~10% CYP (PharmGKB)
 * - Km pattern: CYP < SULT < UGT (Zurlinden & Reisfeld 2016, PMC4799923)
 * - CYP2E1 = 74% of NAPQI in vivo (Manyike et al. 2000)
 * - GSH toxicity at 70% depletion = 30% remaining (Mitchell et al. 1973)
 * - PAPS pool ~6-10x smaller than UDPGA (Klaassen 1997)
 * - NAC within 8h = ~100% protection (Smilkstein 1988)
 * - Alcohol: CYP2E1 2-5x induction, GSH 20-40% depletion (Lieber 1997)
 *
 * KINETIC MODEL: Michaelis-Menten (rate = Vmax * [S] / (Km + [S]))
 * Competing reactions processed simultaneously with proportional allocation.
 *
 * GSH DEPLETION is the critical mechanism:
 *   - Each NAPQI neutralized costs 3 GSH units (cofactorCost=3.0)
 *   - GSH regeneration (0.4/tick) cannot keep up with CYP output at overdose
 *   - When GSH drops below 30% (Mitchell threshold), conjugation fails
 *   - NAPQI accumulates → protein binding → ROS → mitochondrial collapse
 */

const apapScenario = {
  id: 'apap',
  name: 'Acetaminophen Metabolism',
  description: 'Manage hepatocyte metabolism of acetaminophen to prevent liver injury.',

  // ─── Molecules ──────────────────────────────────────────────
  molecules: [
    {
      id: 'APAP',
      name: 'Acetaminophen',
      simpleName: 'Drug (APAP)',
      description: 'Acetaminophen (paracetamol) is a widely used analgesic and antipyretic. It must be metabolized by the liver before it can be eliminated from the body.',
      color: '#4A90D9',
      initialAmount: 0,
      maxAmount: 500,
      category: 'substrate',
    },
    {
      id: 'APAP_glucuronide',
      name: 'APAP-Glucuronide',
      simpleName: 'Glucuronide\nSafe product',
      description: 'A non-toxic, water-soluble conjugate formed when UGT attaches glucuronic acid to APAP. This is the dominant safe clearance pathway (~55% at therapeutic doses) and is excreted in urine.',
      color: '#7ED321',
      initialAmount: 0,
      maxAmount: 1000,
      category: 'product_safe',
    },
    {
      id: 'APAP_sulfate',
      name: 'APAP-Sulfate',
      simpleName: 'Sulfation\nSafe product',
      description: 'A non-toxic, water-soluble conjugate formed when SULT attaches a sulfate group to APAP. Handles ~30% of clearance at therapeutic doses but saturates early in overdose because PAPS cofactor stores are small.',
      color: '#50C878',
      initialAmount: 0,
      maxAmount: 1000,
      category: 'product_safe',
    },
    {
      id: 'NAPQI',
      name: 'N-acetyl-p-benzoquinone imine (NAPQI)',
      simpleName: 'Toxic\nIntermediate',
      description: 'N-acetyl-p-benzoquinone imine. It covalently binds to protein thiol (sulfur) groups — this is the primary toxic event.\n\nDetox vs. failure point: If GSH is available, NAPQI is neutralized. If GSH is depleted, NAPQI binds proteins.\n\nMitochondrial targeting (critical step): Protein adducts form preferentially on mitochondrial proteins, disrupting the electron transport chain, ATP production, and redox balance.\n\nROS generation (secondary but essential): Damaged mitochondria leak electrons, generating superoxide, hydrogen peroxide, and peroxynitrite. ROS amplifies injury, but does not start it.',
      color: '#D0021B',
      initialAmount: 0,
      maxAmount: 100,
      category: 'toxic',
    },
    {
      id: 'APAP_GSH',
      name: 'APAP-GSH Conjugate (Mercapturic acid)',
      simpleName: 'Detox product',
      description: 'The safely neutralized form of NAPQI, produced when glutathione conjugates with NAPQI. This detox product is non-toxic and excreted in urine. Its presence means the GSH defense system is working.',
      color: '#4ECDC4',
      initialAmount: 0,
      maxAmount: 1000,
      category: 'product_safe',
    },
    {
      id: 'PROTEIN_ADDUCTS',
      name: 'Protein Adducts',
      simpleName: 'Protein\nDamage',
      description: 'When GSH is depleted, NAPQI covalently binds to cellular protein thiol (sulfur) groups, forming protein adducts. These adducts form preferentially on mitochondrial proteins, disrupting the electron transport chain, ATP production, and redox balance. This is the primary toxic event that triggers oxidative stress and hepatocyte death.',
      color: '#FF4444',
      initialAmount: 0,
      maxAmount: 100,
      category: 'damage',
    },
    {
      // GSH pool: 80 game units = 100% of normal hepatic stores (~7 mmol real)
      // Toxicity begins at 70% depletion = 30% remaining = 24 units (Mitchell 1973)
      // Engine cofactor limitation kicks in below 30% of maxAmount automatically
      id: 'GSH',
      name: 'Glutathione',
      simpleName: 'Detox capacity (GSH)',
      description: 'Glutathione (GSH) acts like a molecular sponge, using its sulfur-containing tip (SH group) to grab onto toxic NAPQI and neutralize it, forming safe APAP-GSH for excretion. Your liver constantly regenerates GSH, but in overdose, NAPQI production overwhelms the regeneration cycle. When GSH falls below 30% (Mitchell threshold), NAPQI binds directly to liver cell proteins, causing irreversible damage.',
      color: '#F5A623',
      initialAmount: 80,
      maxAmount: 80,
      regenRate: 0.4, // ~200-300 µmol/hr scaled; slow vs consumption at overdose
      category: 'cofactor',
    },
    {
      // PAPS: smallest cofactor pool. Depletes FIRST during overdose.
      // Real: 45-120 µmol, ~6-10x smaller than UDPGA (Klaassen 1997)
      id: 'PAPS',
      name: "3'-Phosphoadenosine-5'-phosphosulfate (PAPS)",
      simpleName: 'PAPS\nSulfur donor',
      description: "PAPS (3\u2032-phosphoadenosine-5\u2032-phosphosulfate) is the activated sulfate donor required for the sulfation pathway of acetaminophen metabolism. PAPS enables a major \"safe pathway\" for detoxification.\n\nWhat PAPS does: PAPS provides a sulfate group (SO\u2084\u00B2\u207B) to acetaminophen. This reaction is catalyzed by SULT (sulfotransferase). Product: APAP-sulfate (APAP-Sul) \u2014 water-soluble, non-toxic, excretable.\n\nPAPS stores are small (~6-10x less than UDPGA) and costly to regenerate (2 ATP per molecule), so sulfation saturates first during overdose.",
      color: '#BD10E0',
      initialAmount: 25,
      maxAmount: 25,
      regenRate: 0.25, // Slow — 2 ATP required per PAPS (Venkatachalam 2003)
      category: 'cofactor',
    },
    {
      // UDPGA: ~5-8x larger than PAPS. Real: 300-600 µmol (Miners 1991)
      id: 'UDPGA',
      name: 'UDP-Glucuronic Acid (UDPGA)',
      simpleName: 'UDPGA\nGlucuronic acid donor',
      description: 'The cofactor required by UGT enzymes to attach glucuronic acid to APAP. Larger stores than PAPS (~5-8x), so glucuronidation sustains longer during overdose before depleting.',
      color: '#8B572A',
      initialAmount: 120,
      maxAmount: 120,
      regenRate: 1.0,
      category: 'cofactor',
    },
  ],

  // ─── Enzymes ────────────────────────────────────────────────
  enzymes: [
    {
      id: 'UGT',
      name: 'UDP - glucuronosyltransferase',
      simpleName: 'Safe pathway 1',
      description: 'UDP-glucuronosyltransferase. The primary safe metabolic enzyme for APAP. Attaches glucuronic acid to make APAP water-soluble for excretion. Handles ~55% of therapeutic dose clearance.',
      activityMultiplier: 1,
    },
    {
      id: 'SULT',
      name: 'Sulfotransferase',
      simpleName: 'Safe pathway 2',
      description: 'Sulfotransferase. Attaches a sulfate group to APAP for safe excretion. Handles ~30% of clearance at therapeutic doses but saturates early because its cofactor (PAPS) is limited.',
      activityMultiplier: 1,
    },
    {
      id: 'CYP2E1',
      name: 'CYP2E1 — Toxic (major)',
      simpleName: 'Toxic pathway (major)',
      description: 'Cytochrome P450 2E1. The primary enzyme that converts APAP to the toxic metabolite NAPQI. Responsible for ~74% of NAPQI formation. Has high affinity (low Km) so it is active even at low APAP levels. Induced by chronic alcohol use.',
      activityMultiplier: 1,
      minor: false,
    },
    {
      id: 'CYP1A2',
      name: 'CYP1A2 — Toxic (minor)',
      simpleName: 'Toxic pathway (minor)',
      description: 'Cytochrome P450 1A2. A minor contributor to NAPQI formation. Induced by smoking and charcoal-grilled foods. Plays a larger role when CYP2E1 is inhibited.',
      activityMultiplier: 1,
      minor: true,
    },
    {
      id: 'CYP3A4',
      name: 'CYP3A4 — Toxic (minor)',
      simpleName: 'Toxic pathway (minor)',
      description: 'Cytochrome P450 3A4. The most abundant CYP in the liver but a minor contributor to NAPQI production from APAP. Important in drug-drug interactions as many medications are metabolized by CYP3A4.',
      activityMultiplier: 1,
      minor: true,
    },
  ],

  // ─── Reactions (Michaelis-Menten) ───────────────────────────
  // rate = vmax * [S] / (km + [S])
  // Km pattern preserves real ranking: CYP (low Km, high affinity) < SULT < UGT (high Km)
  // Vmax pattern: UGT (high capacity) > SULT > CYP (low capacity)
  reactions: [
    {
      id: 'rxn_glucuronidation',
      name: 'Glucuronidation',
      simpleName: 'Safe clearance 1',
      substrate: 'APAP',
      product: 'APAP_glucuronide',
      enzyme: 'UGT',
      cofactor: 'UDPGA',
      cofactorCost: 1.0,
      vmax: 4.0,  // Highest Vmax — dominant safe pathway
      km: 45,     // Highest Km — low affinity (real: 6570 µM)
      toxic: false,
      cofactorDepletionMessage: 'UDP-GA depleted — glucuronidation pathway blocked.',
      cofactorLowMessage: 'UDP-GA running low — glucuronidation slowing down.',
    },
    {
      id: 'rxn_sulfation',
      name: 'Sulfation',
      simpleName: 'Safe clearance 2',
      substrate: 'APAP',
      product: 'APAP_sulfate',
      enzyme: 'SULT',
      cofactor: 'PAPS',
      cofactorCost: 1.5,  // PAPS is expensive — 2 ATP per molecule
      vmax: 1.8,  // Lower capacity; saturates before UGT
      km: 25,     // Medium Km (real: 460 µM)
      toxic: false,
      cofactorDepletionMessage: 'PAPS depleted — sulfation pathway is now blocked.',
      cofactorLowMessage: 'Sulfation capacity decreasing — PAPS stores running low.',
    },
    {
      // CYP2E1: 74% of NAPQI formation in vivo (Manyike 2000)
      id: 'rxn_cyp_oxidation',
      name: 'CYP2E1 Oxidation',
      simpleName: 'Toxic conversion (major)',
      substrate: 'APAP',
      product: 'NAPQI',
      enzyme: 'CYP2E1',
      cofactor: null,
      vmax: 0.18, // Low capacity but no cofactor limitation
      km: 8,      // Low Km = high affinity (real: ~125 µM combined CYP)
      toxic: true,
      minor: false,
      productRatio: 1,
      shuntMessage: 'APAP is being shunted into CYP2E1 → NAPQI production increasing.',
    },
    {
      id: 'rxn_cyp1a2_oxidation',
      name: 'CYP1A2 Oxidation',
      simpleName: 'Toxic conversion (minor)',
      substrate: 'APAP',
      product: 'NAPQI',
      enzyme: 'CYP1A2',
      cofactor: null,
      vmax: 0.04,
      km: 8,
      toxic: true,
      minor: true,
      productRatio: 1,
      shuntMessage: 'CYP1A2 is contributing to NAPQI formation.',
    },
    {
      // CYP3A4: lowest Km in vitro (130 µM, Laine 2009) but low expression
      id: 'rxn_cyp3a4_oxidation',
      name: 'CYP3A4 Oxidation',
      simpleName: 'Toxic conversion (minor)',
      substrate: 'APAP',
      product: 'NAPQI',
      enzyme: 'CYP3A4',
      cofactor: null,
      vmax: 0.03,
      km: 6,
      toxic: true,
      minor: true,
      productRatio: 1,
      shuntMessage: 'CYP3A4 is contributing to NAPQI formation.',
    },
    {
      // GSH conjugation: very fast reaction (k = 3.2e4 M-1 s-1, Jaeschke 2012)
      // GST enhances 2-5x (Coles 1988)
      // cofactorCost=3.0 models the rapid GSH consumption that drives depletion
      id: 'rxn_gsh_conjugation',
      name: 'GSH Conjugation',
      simpleName: 'Toxin neutralization',
      substrate: 'NAPQI',
      product: 'APAP_GSH',
      enzyme: null,
      cofactor: 'GSH',
      cofactorCost: 4.0,  // High cost drives GSH depletion — the key toxicity mechanism
      vmax: 6.0,   // Fast reaction
      km: 3.0,     // High affinity
      toxic: false,
      cofactorDepletionMessage: 'Glutathione depleted — NAPQI is binding to hepatocyte proteins!',
      cofactorLowMessage: 'NAPQI accumulating faster than glutathione can neutralize it.',
    },
    {
      // Protein binding: NAPQI covalently binds to cellular protein thiol groups
      // This competes with GSH conjugation but is much slower when GSH is available
      // Becomes dominant when GSH is depleted
      id: 'rxn_protein_binding',
      name: 'Covalent Protein Binding',
      simpleName: 'Protein damage',
      substrate: 'NAPQI',
      product: 'PROTEIN_ADDUCTS',
      enzyme: null,
      cofactor: null,
      vmax: 0.8,    // Moderate rate
      km: 8.0,      // Lower affinity than GSH conjugation (km=3)
      toxic: true,
      minor: false,
      productRatio: 1,
    },
  ],

  // ─── Damage Trackers ────────────────────────────────────────
  damageTrackers: [
    {
      id: 'ros_level',
      name: 'ROS Level',
      simpleName: 'Oxidative stress',
      initialValue: 0,
      min: 0,
      max: 100,
      color: '#FF6B6B',
    },
    {
      // Irreversible above ~58% depolarization (Hu et al. 2016)
      id: 'mitochondrial_integrity',
      name: 'Mitochondrial Integrity',
      simpleName: 'Mitochondria health',
      initialValue: 100,
      min: 0,
      max: 100,
      color: '#9B59B6',
    },
    {
      // Liver failure at 80-90% hepatocyte loss; >99% necrotic death (Gujral 2002)
      id: 'hepatocyte_health',
      name: 'Hepatocyte Health',
      simpleName: 'Cell health',
      initialValue: 100,
      min: 0,
      max: 100,
      color: '#2ECC71',
    },
  ],

  // ─── Damage Rules ───────────────────────────────────────────
  // Cascade: NAPQI → ROS → Mitochondrial damage → Cell death
  damageRules: [
    {
      // NAPQI disrupts ETC Complex III → superoxide (Nguyen 2020)
      id: 'napqi_ros',
      source: 'NAPQI',
      target: 'ros_level',
      threshold: 0.5,
      rate: 1.2,
      direction: 'increase',
      recoveryRate: 0.4,
      feedbackMessage: 'ROS increasing — NAPQI is disrupting the electron transport chain.',
    },
    {
      // ROS opens mitochondrial permeability transition pore (Hu 2016)
      id: 'ros_mito',
      source: 'ros_level',
      target: 'mitochondrial_integrity',
      threshold: 20,
      rate: 0.3,
      direction: 'decrease',
      recoveryRate: 0.04,
      feedbackMessage: 'Mitochondrial membranes destabilizing — permeability transition pores opening.',
    },
    {
      // NAPQI covalent protein binding (only significant when GSH depleted)
      id: 'napqi_direct_damage',
      source: 'NAPQI',
      target: 'hepatocyte_health',
      threshold: 2.0,
      rate: 0.35,
      direction: 'decrease',
      recoveryRate: 0.02,
      feedbackMessage: 'NAPQI is covalently binding to hepatocyte proteins.',
    },
    {
      // Sustained ROS → oncotic necrosis (>99% necrotic, Gujral 2002)
      id: 'ros_health',
      source: 'ros_level',
      target: 'hepatocyte_health',
      threshold: 30,
      rate: 0.3,
      direction: 'decrease',
      recoveryRate: 0.02,
      feedbackMessage: 'Sustained oxidative stress is causing hepatocyte necrosis.',
    },
  ],

  // ─── Win/Lose Conditions ────────────────────────────────────
  loseConditions: [
    {
      trackerId: 'mitochondrial_integrity',
      type: 'below',
      threshold: 10,
      message: 'Mitochondrial collapse — irreversible permeability transition. The hepatocyte cannot recover.',
    },
    {
      trackerId: 'hepatocyte_health',
      type: 'below',
      threshold: 5,
      message: 'Critical hepatocyte injury — oncotic necrosis. The cell has died.',
    },
  ],

  winConditions: [
    {
      type: 'molecule_below',
      moleculeId: 'APAP',
      threshold: 0.5,
    },
    {
      type: 'molecule_below',
      moleculeId: 'NAPQI',
      threshold: 0.5,
    },
    {
      type: 'damage_above',
      trackerId: 'hepatocyte_health',
      threshold: 15,
    },
    {
      type: 'damage_above',
      trackerId: 'mitochondrial_integrity',
      threshold: 15,
    },
  ],

  // ─── Scenario Modifiers ─────────────────────────────────────
  defaultModifiers: {
    alcohol: false,
    lowGSH: false,
    cyp2e1Fast: false,
    cyp2e1Slow: false,
  },

  modifiers: [
    {
      // CYP2E1 2-5x induction (Lieber 1997), GSH 20-40% depletion (Fernandez-Checa 1997)
      // Toxicity threshold drops 2-3x (Zimmerman 1995)
      id: 'alcohol',
      name: 'Alcohol Exposure',
      simpleName: 'Alcohol',
      description: 'Chronic alcohol induces CYP2E1 (2-5x) and depletes GSH 20-40%. Toxic threshold drops 2-3 fold.',
      effects: [
        { target: 'enzyme', enzymeId: 'CYP2E1', multiplier: 3.0 },
        { target: 'molecule', moleculeId: 'GSH', multiplier: 0.65 },
        { target: 'regeneration', moleculeId: 'GSH', multiplier: 0.7 },
      ],
    },
    {
      id: 'lowGSH',
      name: 'Low GSH Baseline',
      simpleName: 'Low detox capacity',
      description: 'Fasting or malnutrition reduces glutathione stores by 40-60%.',
      effects: [
        { target: 'molecule', moleculeId: 'GSH', multiplier: 0.45 },
        { target: 'regeneration', moleculeId: 'GSH', multiplier: 0.5 },
      ],
    },
    {
      // CYP2E1*5B c2: ~1.5-2x activity (Hayashi 1991)
      // Freq: 1-5% Caucasian, 20-30% East Asian
      id: 'cyp2e1Fast',
      name: 'CYP2E1 Fast Metabolizer',
      simpleName: 'Fast toxic metabolism',
      description: 'Polymorphism (*5B c2) increases CYP2E1 ~1.5-2x. Freq: 1-5% Caucasian, 20-30% East Asian.',
      effects: [
        { target: 'enzyme', enzymeId: 'CYP2E1', multiplier: 1.8 },
        { target: 'enzyme', enzymeId: 'CYP1A2', multiplier: 1.2 },
        { target: 'enzyme', enzymeId: 'CYP3A4', multiplier: 1.2 },
      ],
    },
    {
      id: 'cyp2e1Slow',
      name: 'CYP2E1 Slow Metabolizer',
      simpleName: 'Slow toxic metabolism',
      description: 'Genetic variant decreases CYP2E1 activity. Reduced NAPQI formation is protective.',
      effects: [
        { target: 'enzyme', enzymeId: 'CYP2E1', multiplier: 0.4 },
        { target: 'enzyme', enzymeId: 'CYP1A2', multiplier: 0.7 },
        { target: 'enzyme', enzymeId: 'CYP3A4', multiplier: 0.7 },
      ],
    },
  ],

  // ─── Interventions ──────────────────────────────────────────
  interventions: [
    {
      // <8h = ~100% protection; 8-16h = 70-80%; >24h = limited (Smilkstein 1988)
      // Provides cysteine → GSH synthesis. Recovery: 40-60% in 2-4h (Lauterburg 1983)
      id: 'nac',
      name: 'N-Acetylcysteine (NAC)',
      simpleName: 'Antidote (NAC)',
      description: 'Restores GSH via cysteine donation. Best within 8h. Effectiveness drops sharply after.',
      activationMessage: 'NAC administered — cysteine supply restored, boosting glutathione synthesis.',
      effects: [
        { type: 'boost_regeneration', moleculeId: 'GSH', amount: 3.5 },
      ],
    },
  ],

  // ─── Dose Presets ───────────────────────────────────────────
  // Real dose → game units (preserving relative hepatic load):
  //   325mg therapeutic, 1g high, 4g overdose (>150mg/kg), 10g massive
  dosePresets: [
    { label: 'Therapeutic (325mg)', amount: 15, simpleName: 'Normal dose' },
    { label: 'High therapeutic (1g)', amount: 45, simpleName: 'High dose' },
    { label: 'Overdose (4g)', amount: 180, simpleName: 'Overdose' },
    { label: 'Massive overdose (10g)', amount: 450, simpleName: 'Massive overdose' },
  ],

  // ─── Pathway Layout (for visualization) ─────────────────────
  pathwayLayout: {
    nodes: [
      { id: 'APAP', x: 400, y: 50, type: 'molecule' },
      { id: 'UGT', x: 140, y: 170, type: 'enzyme' },
      { id: 'SULT', x: 320, y: 170, type: 'enzyme' },
      { id: 'CYP2E1', x: 520, y: 170, type: 'enzyme' },
      { id: 'CYP1A2', x: 660, y: 170, type: 'enzyme' },
      { id: 'CYP3A4', x: 720, y: 250, type: 'enzyme' },
      { id: 'UDPGA_node', x: 50, y: 240, type: 'cofactor', moleculeId: 'UDPGA' },
      { id: 'PAPS_node', x: 400, y: 240, type: 'cofactor', moleculeId: 'PAPS' },
      { id: 'APAP_glucuronide', x: 80, y: 310, type: 'molecule' },
      { id: 'APAP_sulfate', x: 250, y: 310, type: 'molecule' },
      { id: 'NAPQI', x: 580, y: 340, type: 'molecule' },
      { id: 'GSH_node', x: 420, y: 420, type: 'cofactor', moleculeId: 'GSH' },
      { id: 'APAP_GSH', x: 480, y: 500, type: 'molecule' },
      { id: 'PROTEIN_ADDUCTS', x: 700, y: 500, type: 'molecule' },
    ],
    edges: [
      { from: 'APAP', to: 'UGT', reactionId: 'rxn_glucuronidation' },
      { from: 'APAP', to: 'SULT', reactionId: 'rxn_sulfation' },
      { from: 'APAP', to: 'CYP2E1', reactionId: 'rxn_cyp_oxidation' },
      { from: 'APAP', to: 'CYP1A2', reactionId: 'rxn_cyp1a2_oxidation' },
      { from: 'APAP', to: 'CYP3A4', reactionId: 'rxn_cyp3a4_oxidation' },
      { from: 'UDPGA_node', to: 'UGT', reactionId: 'rxn_glucuronidation' },
      { from: 'PAPS_node', to: 'SULT', reactionId: 'rxn_sulfation' },
      { from: 'UGT', to: 'APAP_glucuronide', reactionId: 'rxn_glucuronidation' },
      { from: 'SULT', to: 'APAP_sulfate', reactionId: 'rxn_sulfation' },
      { from: 'CYP2E1', to: 'NAPQI', reactionId: 'rxn_cyp_oxidation' },
      { from: 'CYP1A2', to: 'NAPQI', reactionId: 'rxn_cyp1a2_oxidation' },
      { from: 'CYP3A4', to: 'NAPQI', reactionId: 'rxn_cyp3a4_oxidation' },
      { from: 'NAPQI', to: 'APAP_GSH', reactionId: 'rxn_gsh_conjugation' },
      { from: 'GSH_node', to: 'APAP_GSH', reactionId: 'rxn_gsh_conjugation' },
      { from: 'NAPQI', to: 'PROTEIN_ADDUCTS', reactionId: 'rxn_protein_binding' },
    ],
  },
};

export default apapScenario;
